import { OverflowMenuHorizontal24 } from "@carbon/icons-react";
import { Dropdown, Menu } from "antd";
import { ColumnProps } from "antd/lib/table";
import { API_EMAIL_REMINDER_CONFIG_PREFIX } from "config/api-consts";
import { EMAIL_REMINDER_CONFIG_ROUTE } from "config/route-consts";
import { formatDate } from "helpers/date-time";
import { renderMasterIndex } from "helpers/table";
import {
  EmailRemindConfig,
  EmailRemindConfigFilter,
} from "models/EmailRemindConfig";
import { Moment } from "moment";
import React from "react";
import { useTranslation } from "react-i18next";
import {
  LayoutCell,
  LayoutHeader,
  OneLineText,
  StatusLine,
} from "react3l-ui-library";
import { emailRemindConfigRepository } from "repositories/email-remind-config-repository";
import { authorizationService } from "services/common-services/authorization-service";
import { filterService } from "services/page-services/filter-service";
import { listService } from "services/page-services/list-service";
import { masterService } from "services/page-services/master-service";
import { queryStringService } from "services/page-services/query-string-service";
import {
  getAntOrderType,
  tableService,
} from "services/page-services/table-service";
import nameof from "ts-nameof.macro";

function useEmailRemindConfigMasterHook() {
  const [translate] = useTranslation();

  const { validAction } = authorizationService.useAuthorizedAction(
    EMAIL_REMINDER_CONFIG_ROUTE.split("/")[
      EMAIL_REMINDER_CONFIG_ROUTE.split("/").length - 1
    ],
    API_EMAIL_REMINDER_CONFIG_PREFIX
  );

  const [modelFilter, dispatch] = queryStringService.useQueryString(
    EmailRemindConfigFilter,
    { skip: 0, take: 10 }
  );
  const [visible, setVisible] = React.useState<boolean>(false);

  const {
    value: filter,
    handleChangeAllFilter,
    handleChangeSelectFilter,
    handleChangeInputSearch,
  } = filterService.useFilter(modelFilter, dispatch);

  const { list, count, loadingList, handleResetList } = listService.useList(
    emailRemindConfigRepository.list,
    emailRemindConfigRepository.count,
    filter,
    dispatch
  );

  const { handleTableChange, handlePagination } = tableService.useTable(
    filter,
    handleChangeAllFilter
  );

  const {
    handleAction,
    handleBulkAction,
    canBulkAction,
    selectedRowKeys,
    setSelectedRowKeys,
  } = listService.useRowSelection(
    emailRemindConfigRepository.delete,
    emailRemindConfigRepository.bulkDelete,
    null,
    null,
    null,
    handleResetList
  );

  const { handleGoCreate, handleGoDetail, handleGoPreview, handleDeleteItem } =
    masterService.useMasterAction(EMAIL_REMINDER_CONFIG_ROUTE, handleAction);

  const menuAction = React.useCallback(
    (id: number, record: EmailRemindConfig) => (
      <Menu>
        <Menu.Item key="1">
          <div className="ant-action-menu" onClick={handleGoPreview(id)}>
            {translate("general.actions.view")}
          </div>
        </Menu.Item>
        {validAction("update") && (
          <Menu.Item key="2">
            <div
              className="ant-action-menu text-center"
              onClick={handleGoDetail(id)}
            >
              {translate("general.actions.edit")}
            </div>
          </Menu.Item>
        )}
        {validAction("delete") && (
          <Menu.Item key="3">
            <div
              className="ant-action-menu text-center"
              onClick={handleDeleteItem(record)}
            >
              {translate("general.actions.delete")}
            </div>
          </Menu.Item>
        )}
      </Menu>
    ),
    [handleGoDetail, handleGoPreview, handleDeleteItem, translate, validAction]
  );

  const columns: ColumnProps<EmailRemindConfig>[] = React.useMemo(
    () => [
      {
        title: (
          <div className="text-center gradient-text">
            {translate("general.columns.orderNumber")}
          </div>
        ),
        key: "index",
        width: 80,
        align: "center",
        fixed: "left",
        render: renderMasterIndex<EmailRemindConfig>(),
      },
      {
        title: ({ sortColumns }) => {
          const sortedColumn = sortColumns?.find(
            ({ column }) => column.key === "name"
          );
          return (
            <LayoutHeader
              orderType="left"
              title={translate("emailRemindConfig.master.remindType")}
              sortedColumn={sortedColumn}
              isSorter
            />
          );
        },
        key: nameof(list[0].name),
        dataIndex: nameof(list[0].name),
        sorter: true,
        ellipsis: true,
        sortOrder: getAntOrderType<EmailRemindConfig, EmailRemindConfigFilter>(
          filter,
          nameof(list[0].name)
        ),
        render(...params: [any, EmailRemindConfig, number]) {
          return (
            <LayoutCell orderType="left" tableSize="sm">
              <OneLineText value={params[0]} />
            </LayoutCell>
          );
        },
      },
      {
        title: () => {
          return (
            <div>
              <LayoutHeader
                orderType="left"
                title={translate("emailRemindConfig.createdBy")}
              />
            </div>
          );
        },
        width: 140,
        key: nameof(list[0].createdBy),
        dataIndex: nameof(list[0].createdBy),
        render(creator: any) {
          return (
            <LayoutCell orderType="left" tableSize="md">
              <OneLineText value={creator?.displayName} countCharacters={30} />
            </LayoutCell>
          );
        },
      },
      {
        title: () => {
          return (
            <div>
              <LayoutHeader
                orderType="left"
                title={translate("emailRemindConfig.createdAt")}
                // position="center"
              />
            </div>
          );
        },
        width: 120,
        key: nameof(list[0].createdAt),
        dataIndex: nameof(list[0].createdAt),
        render(...params: [Moment, EmailRemindConfig, number]) {
          return (
            <LayoutCell orderType="left" tableSize="md" position="center">
              <OneLineText value={params[0] ? formatDate(params[0]) : ""} />
            </LayoutCell>
          );
        },
      },
      {
        title: () => {
          return (
            <div>
              <LayoutHeader
                orderType="left"
                title={translate("emailRemindConfig.updatedBy")}
              />
            </div>
          );
        },
        width: 140,
        key: nameof(list[0].updatedBy),
        dataIndex: nameof(list[0].updatedBy),
        render(updatedBy: any) {
          return (
            <LayoutCell orderType="left" tableSize="md">
              <OneLineText
                value={updatedBy?.displayName}
                countCharacters={30}
              />
            </LayoutCell>
          );
        },
      },
      {
        title: () => {
          return (
            <div>
              <LayoutHeader
                orderType="left"
                title={translate("emailRemindConfig.updatedAt")}
                // position="center"
              />
            </div>
          );
        },
        width: 120,
        key: nameof(list[0].updatedAt),
        dataIndex: nameof(list[0].updatedAt),
        render(...params: [Moment, EmailRemindConfig, number]) {
          return (
            <LayoutCell orderType="left" tableSize="md" position="center">
              <OneLineText
                value={params[0] ? formatDate(params[0]) : ""}
                countCharacters={30}
              />
            </LayoutCell>
          );
        },
      },
      {
        title: ({ sortColumns }) => {
          const sortedColumn = sortColumns?.find(
            ({ column }) => column.key === "statusId"
          );
          return (
            <LayoutHeader
              orderType="left"
              title={translate("emailRemindConfig.master.status")}
              sortedColumn={sortedColumn}
              isSorter
            />
          );
        },
        key: nameof(list[0].statusId),
        dataIndex: nameof(list[0].statusId),
        sorter: true,
        sortOrder: getAntOrderType<EmailRemindConfig, EmailRemindConfigFilter>(
          filter,
          nameof(list[0].statusId)
        ),
        width: 150,
        ellipsis: true,
        render(...params: [number, EmailRemindConfig, number]) {
          return (
            <LayoutCell orderType="left" tableSize="md">
              <StatusLine
                value={params[1]?.statusName}
                color={params[0] === 1 ? "#24A148" : "#DA1E28"}
              />
            </LayoutCell>
          );
        },
      },
      {
        key: "action",
        dataIndex: nameof(list[0].id),
        fixed: "right",
        width: 80,
        align: "center",
        render(id: number, record: EmailRemindConfig) {
          return (
            <div className="d-flex justify-content-center button-action-table">
              <Dropdown
                overlay={menuAction(id, record)}
                trigger={["click"]}
                placement="bottom"
                arrow
              >
                <OverflowMenuHorizontal24 className="btn-missions-blue" />
              </Dropdown>
            </div>
          );
        },
      },
    ],
    [list, filter, translate, menuAction]
  );

  const handleCancel = React.useCallback(() => {
    setVisible(false);
  }, []);

  return {
    translate,
    handleChangeSelectFilter,
    handleChangeInputSearch,
    handleTableChange,
    handlePagination,
    handleCancel,
    handleGoCreate,
    handleBulkAction,
    setSelectedRowKeys,
    setVisible,
    handleChangeAllFilter,
    validAction,
    filter,
    canBulkAction,
    selectedRowKeys,
    columns,
    list,
    count,
    loadingList,
    visible,
  };
}

export default useEmailRemindConfigMasterHook;
